import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/lib/i18n";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useSurveyStore } from "@/stores/surveyStore";
import { Star } from "lucide-react";
import heroProductSet from "@/assets/hero-product-set.jpg";

export function Hero() {
  const { t } = useLanguage();
  const expandFromBubble = useSurveyStore(state => state.expandFromBubble);
  const { ref: textRef, isVisible: textVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: imgRef, isVisible: imgVisible } = useScrollReveal({ threshold: 0.1, delay: 150 });
  
  const scrollToProducts = () => {
    document.getElementById('products')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  
  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-section-gradient" aria-label="Hero">
      {/* Premium liquid gel decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[15%] left-[8%] w-24 h-24 rounded-full bg-gradient-to-br from-pink-200/40 to-pink-300/25 blur-xl animate-float"></div>
        <div className="absolute top-[35%] right-[12%] w-32 h-32 rounded-full bg-gradient-to-br from-pink-300/30 to-pink-200/20 blur-2xl animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-[25%] left-[15%] w-40 h-40 rounded-full bg-gradient-to-br from-pink-200/25 to-pink-100/20 blur-2xl animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-[60%] right-[20%] w-20 h-20 rounded-full bg-gradient-to-br from-pink-200/30 to-pink-100/25 blur-xl animate-float" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-[20%] left-[30%] w-48 h-1 bg-gradient-to-r from-transparent via-white/40 to-transparent rounded-full rotate-12"></div>
        <div className="absolute bottom-[35%] right-[25%] w-32 h-0.5 bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full -rotate-12"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-200/15 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-pink-100/12 rounded-full blur-[120px]"></div>
      </div>

      <div className="container relative z-10 px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Text content */}
          <div
            ref={textRef}
            className={`text-center lg:text-start order-2 lg:order-1 scroll-reveal ${textVisible ? 'visible' : ''}`}
          >
            {/* Premium Badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mb-8" dir="ltr">
              <Badge variant="outline" className="px-4 py-2 text-sm font-medium bg-white/70 backdrop-blur-sm border-border text-foreground shadow-sm">
                {t('hero.badge.pdrn')}
              </Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm font-medium bg-white/70 backdrop-blur-sm border-border text-foreground shadow-sm">
                {t('hero.badge.glassSkin')}
              </Badge>
              <Badge variant="outline" className="px-4 py-2 text-sm font-medium bg-white/70 backdrop-blur-sm border-border text-foreground shadow-sm">
                {t('hero.badge.korean')}
              </Badge>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-4 tracking-tight leading-tight bg-gradient-to-r from-primary via-pink-400 to-primary bg-clip-text text-transparent">
              {t('hero.title')}
            </h1>

            {/* Social Proof */}
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-6">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm text-muted-foreground font-medium">
                {t('hero.socialProof')}
              </span>
            </div>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 lg:me-auto mb-10 leading-relaxed hero-subtitle">
              {t('hero.subtitle')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
              <Button 
                size="lg" 
                onClick={scrollToProducts} 
                className="bg-gradient-button text-white shadow-lg shadow-primary/20 transition-all duration-300 text-base px-10 py-6 rounded-full font-bold tracking-wide hover:opacity-90 cta-pulse"
              >
                {t('hero.cta')}
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => expandFromBubble()}
                className="border-primary/30 text-primary hover:bg-primary/5 text-base px-8 py-6 rounded-full font-bold tracking-wide"
              >
                {t('hero.ctaSurvey')}
              </Button>
            </div>
          </div>

          {/* Right side - Hero Image */}
          <div
            ref={imgRef}
            className={`order-1 lg:order-2 flex justify-center lg:justify-end scroll-reveal ${imgVisible ? 'visible' : ''}`}
          >
            <div className="relative w-full max-w-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-300/20 to-pink-200/15 blur-3xl scale-110 rounded-3xl"></div>
              <img 
                src={heroProductSet} 
                alt="PDRN skincare collection — Korean salmon DNA serum, sheet masks, and capsule cream for glass skin" 
                className="relative w-full h-auto rounded-2xl shadow-2xl shadow-primary/15"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
